import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { Crest } from '@/components/Brand';
import { toast } from 'sonner';
import { ASSETS } from '@/lib/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { loginEmail, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate(location.state?.from || '/dashboard'); }, [user, navigate, location]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginEmail(email, password);
      toast.success('Welcome back.');
      const isAdmin = u.roles?.some(r => ['super_admin','registrar','billing','support','instructor','faculty'].includes(r));
      navigate(location.state?.from || (isAdmin ? '/admin' : '/dashboard'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid credentials');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-trjlight flex" data-testid="login-page">
      <div className="hidden lg:flex w-1/2 bg-navy relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 hero-grain opacity-60" />
        <div className="relative text-center">
          <img src={ASSETS.shield} alt="" className="h-40 mx-auto crest-glow" />
          <h2 className="font-display font-semibold text-3xl text-white mt-8">Welcome back, student.</h2>
          <p className="font-serif italic text-gold mt-3 text-lg">Continue your journey.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-6"><Crest size="lg" className="mx-auto" /></div>
          <h1 className="font-display font-semibold text-3xl text-navy">Log in</h1>
          <p className="text-slate-600 mt-2">Access your dashboard and continue learning.</p>

          <button onClick={startGoogleAuth} data-testid="google-login-btn"
            className="mt-8 w-full h-12 border border-slate-300 bg-white rounded-md flex items-center justify-center gap-3 hover:border-navy hover:shadow transition">
            <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16 4 9.1 8.5 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.5l-6.5-5.3c-2 1.5-4.6 2.4-7.5 2.4-5.2 0-9.7-3.3-11.3-8l-6.5 5C9 39.5 16 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.5l6.5 5.3c-.4.4 7-5 7-14.8 0-1.2-.1-2.3-.4-3.5z"/></svg>
            <span className="font-medium text-navy">Continue with Google</span>
          </button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> or <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email"
                value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password"
                value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" disabled={busy} className="btn-navy w-full h-12" data-testid="login-submit">
              {busy ? 'Signing in…' : 'Log in'}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            New to TRJ Academy?{' '}
            <Link to="/register" className="text-navy font-semibold hover:text-gold" data-testid="register-link">Create an account</Link>
          </p>
          <p className="mt-2 text-sm text-slate-500 text-center">
            <Link to="/forgot-password" className="text-slate-500 hover:text-navy" data-testid="forgot-link">Forgot your password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
